import { RateOwnerType } from '@treadinc/horizon-api-spec';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { RateBasicWithValue } from '~hooks/useRates/models';
import { useRates } from '~hooks/useRates/useRates';
import { rateToRateBasicWithValue } from '~pages/Sales/Orders/NewOrderForm/utils';
import type { Nullable } from '~types/Nullable';

export type FetchRatesArgs = { query: string; cursor: string };

/**
 * Shape returned by each rate-fetch callback. Mirrors the legacy `fetchRates`
 * return value — `<FreightRates/>` destructures `.data` (basic rates for the
 * paginated dropdown) and `.pagination` (the cursor for the next page). When
 * the watched company isn't set yet the legacy form returns `undefined`; the
 * FreightRates pagination tolerates that and skips the list update.
 */
export type FetchRatesResponse =
  | {
      data: RateBasicWithValue[];
      pagination?: { limit?: number; after?: string | null; before?: string | null };
    }
  | undefined;

export type FetchRatesCallback = (args?: FetchRatesArgs) => Promise<FetchRatesResponse>;

/**
 * Preloaded rate data shared across the v2 form via {@link
 * ProjectFormV2Context}. This replaced an earlier empty stub with the real
 * fetch logic that drives both the rate-dropdown lists and the per-owner
 * "primary rate" defaults the panel feeds into
 * `defaultCustomerRateDetails` / `defaultVendorRateDetails` /
 * `defaultDriverRateDetails`.
 *
 * `status` is a discriminated union: `'loading'` until the initial customer
 * / vendor / driver fetches complete, then `'ready'`. Panels can suppress
 * rate widgets while loading to avoid an empty-state flicker.
 */
export type ProjectFormV2Preloads = {
  status: 'loading' | 'ready';
  customerRates: RateBasicWithValue[];
  vendorRates: RateBasicWithValue[];
  driverRates: RateBasicWithValue[];
  primaryCustomerRate: Nullable<RateBasicWithValue>;
  primaryVendorRate: Nullable<RateBasicWithValue>;
  primaryDriverRate: Nullable<RateBasicWithValue>;
  fetchCustomerRates: FetchRatesCallback;
  fetchVendorRates: FetchRatesCallback;
  fetchDriverRates: FetchRatesCallback;
};

type UsePreloadsArgs = {
  companyId: Nullable<string> | undefined;
  accountId: Nullable<string> | undefined;
};

const EMPTY_FETCH_RESULT: FetchRatesResponse = undefined;

export function useProjectFormV2Preloads({
  companyId,
  accountId,
}: UsePreloadsArgs): ProjectFormV2Preloads {
  const { getAccountRatesUsingOwnerType } = useRates();

  const [customerRates, setCustomerRates] = useState<RateBasicWithValue[]>([]);
  const [vendorRates, setVendorRates] = useState<RateBasicWithValue[]>([]);
  const [driverRates, setDriverRates] = useState<RateBasicWithValue[]>([]);
  const [primaryCustomerRate, setPrimaryCustomerRate] =
    useState<Nullable<RateBasicWithValue>>(null);
  const [primaryVendorRate, setPrimaryVendorRate] =
    useState<Nullable<RateBasicWithValue>>(null);
  const [primaryDriverRate, setPrimaryDriverRate] =
    useState<Nullable<RateBasicWithValue>>(null);
  const [fetchedOwnerTypes, setFetchedOwnerTypes] = useState<ReadonlySet<RateOwnerType>>(
    () => new Set(),
  );

  // Track the latest companyId/accountId in refs so the fetch callbacks
  // can be identity-stable. `<FreightRates/>` registers the callbacks once
  // on mount and reuses them across typeahead pagination; if we recreated
  // them on every companyId change the FreightRates internals would
  // re-subscribe their initial-load effect and double-fire requests.
  const companyIdRef = useRef<Nullable<string> | undefined>(companyId);
  const accountIdRef = useRef<Nullable<string> | undefined>(accountId);
  useEffect(() => {
    companyIdRef.current = companyId;
    accountIdRef.current = accountId;
  }, [companyId, accountId]);

  // Tracks fetches that have STARTED (not just completed) per ownerType
  // for the current companyId. Without this, the initial-load effect would
  // refire VENDOR/DRIVER between CUSTOMER's response landing (which mutates
  // fetchedOwnerTypes) and the VENDOR/DRIVER responses landing, producing
  // duplicate network requests on a single mount.
  const inFlightOwnerTypesRef = useRef<Set<RateOwnerType>>(new Set());

  const fetchForOwner = useCallback(
    async (
      ownerType: RateOwnerType,
      args?: FetchRatesArgs,
    ): Promise<FetchRatesResponse> => {
      const currentCompanyId = companyIdRef.current;
      if (!currentCompanyId) return EMPTY_FETCH_RESULT;

      inFlightOwnerTypesRef.current.add(ownerType);
      // Mark this owner type resolved (once) for the current company. Called on
      // BOTH success and failure: `status` only flips to 'ready' once all three
      // owner types are in `fetchedOwnerTypes`, so a failed fetch that never
      // marked itself would pin `status` at 'loading' forever (the initial-load
      // effect fires `void fetchForOwner()`, so there's no retry on rejection).
      const markOwnerFetched = () =>
        setFetchedOwnerTypes((prev) => {
          if (prev.has(ownerType)) return prev;
          const next = new Set(prev);
          next.add(ownerType);
          return next;
        });
      try {
        const response = await getAccountRatesUsingOwnerType({
          companyId: currentCompanyId,
          ownerType,
          includeDefaultRates: true,
          accountId: accountIdRef.current ?? undefined,
          query: args?.query,
          cursor: args?.cursor,
          limit: 15,
        });

        if (!response) return EMPTY_FETCH_RESULT;

        const { basicRates, primaryRate } = response.data.reduce(
          (acc, rate) => {
            const basic = rateToRateBasicWithValue(rate);
            acc.basicRates.push(basic);
            if (rate.isPrimary) acc.primaryRate = basic;
            return acc;
          },
          {
            basicRates: [] as RateBasicWithValue[],
            primaryRate: null as Nullable<RateBasicWithValue>,
          },
        );

        if (ownerType === RateOwnerType.CUSTOMER) {
          setCustomerRates(basicRates);
          setPrimaryCustomerRate(primaryRate);
        } else if (ownerType === RateOwnerType.VENDOR) {
          setVendorRates(basicRates);
          setPrimaryVendorRate(primaryRate);
        } else if (ownerType === RateOwnerType.DRIVER) {
          setDriverRates(basicRates);
          setPrimaryDriverRate(primaryRate);
        }
        markOwnerFetched();

        return { data: basicRates, pagination: response.pagination };
      } catch {
        // getAccountRatesUsingOwnerType → connection.handleRequestError already
        // surfaced the failure via toast before throwing, so the user is
        // informed. Mark the owner type fetched anyway to unblock `status`,
        // and degrade to an empty result so on-demand callers (rate typeaheads)
        // show "no options" instead of leaking an unhandled rejection.
        markOwnerFetched();
        return EMPTY_FETCH_RESULT;
      } finally {
        inFlightOwnerTypesRef.current.delete(ownerType);
      }
    },
    [getAccountRatesUsingOwnerType],
  );

  const fetchCustomerRates = useCallback<FetchRatesCallback>(
    (args) => fetchForOwner(RateOwnerType.CUSTOMER, args),
    [fetchForOwner],
  );
  const fetchVendorRates = useCallback<FetchRatesCallback>(
    (args) => fetchForOwner(RateOwnerType.VENDOR, args),
    [fetchForOwner],
  );
  const fetchDriverRates = useCallback<FetchRatesCallback>(
    (args) => fetchForOwner(RateOwnerType.DRIVER, args),
    [fetchForOwner],
  );

  // Reset caches + markers when the watched company changes so the next
  // tick refetches under the new company. Mirrors the implicit behavior of
  // legacy ProjectForm.tsx whose fetchRates closure depends on company id.
  const previousCompanyIdRef = useRef<Nullable<string> | undefined>(companyId);
  useEffect(() => {
    if (previousCompanyIdRef.current !== companyId) {
      previousCompanyIdRef.current = companyId;
      inFlightOwnerTypesRef.current = new Set();
      setFetchedOwnerTypes(new Set());
      setCustomerRates([]);
      setVendorRates([]);
      setDriverRates([]);
      setPrimaryCustomerRate(null);
      setPrimaryVendorRate(null);
      setPrimaryDriverRate(null);
    }
  }, [companyId]);

  // Mirrors the legacy form: kick off the initial 3-owner
  // fetch once the watched company is available. The in-flight ref + the
  // `fetchedOwnerTypes` Set together gate re-entry so the effect doesn't
  // duplicate requests when one fetch resolves mid-flight.
  useEffect(() => {
    if (!companyId) return;

    const owners: RateOwnerType[] = [
      RateOwnerType.CUSTOMER,
      RateOwnerType.VENDOR,
      RateOwnerType.DRIVER,
    ];
    for (const ownerType of owners) {
      if (
        !fetchedOwnerTypes.has(ownerType) &&
        !inFlightOwnerTypesRef.current.has(ownerType)
      ) {
        const fetcher =
          ownerType === RateOwnerType.CUSTOMER
            ? fetchCustomerRates
            : ownerType === RateOwnerType.VENDOR
              ? fetchVendorRates
              : fetchDriverRates;
        void fetcher();
      }
    }
  }, [
    companyId,
    fetchedOwnerTypes,
    fetchCustomerRates,
    fetchVendorRates,
    fetchDriverRates,
  ]);

  const allFetched =
    fetchedOwnerTypes.has(RateOwnerType.CUSTOMER) &&
    fetchedOwnerTypes.has(RateOwnerType.VENDOR) &&
    fetchedOwnerTypes.has(RateOwnerType.DRIVER);

  // 'ready' once either (a) all three owner-type fetches resolved, or (b)
  // no companyId is set yet (nothing to load). The latter prevents a
  // permanent 'loading' state on the create-project flow before the user
  // picks a company.
  const status: ProjectFormV2Preloads['status'] =
    !companyId || allFetched ? 'ready' : 'loading';

  // Memoize the returned shape so consumers (notably
  // `ProjectFormV2Context.tsx`'s value `useMemo`) get a stable identity when
  // none of the underlying fields change. Without this, every Provider
  // re-render produces a fresh preloads object — which then invalidates the
  // context-value `useMemo` and forces every panel that reads context to
  // re-render even when nothing meaningful has changed.
  return useMemo<ProjectFormV2Preloads>(
    () => ({
      status,
      customerRates,
      vendorRates,
      driverRates,
      primaryCustomerRate,
      primaryVendorRate,
      primaryDriverRate,
      fetchCustomerRates,
      fetchVendorRates,
      fetchDriverRates,
    }),
    [
      status,
      customerRates,
      vendorRates,
      driverRates,
      primaryCustomerRate,
      primaryVendorRate,
      primaryDriverRate,
      fetchCustomerRates,
      fetchVendorRates,
      fetchDriverRates,
    ],
  );
}
